import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson } from '@factorialco/f0-react'
import {
  LocalVideoTrack,
  VideoPresets,
  createLocalVideoTrack,
  facingModeFromLocalTrack,
} from 'livekit-client'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'

interface VideoContainerProps {
  openPrivacyPolicy?: boolean
  loading?: boolean
  videoEnabled: boolean
  videoDeviceId: string
  backgroundType: string
}

export const PrejoinVideoContainer = ({
  openPrivacyPolicy = false,
  loading = false,
  videoEnabled,
  videoDeviceId,
  backgroundType,
}: VideoContainerProps) => {
  const {
    access: { fullName: username, firstName, lastName, avatar },
  } = useCurrentSession()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | null>(null)
  const videoTrackRef = useRef<LocalVideoTrack | null>(null)

  // Keep ref in sync with state
  useEffect(() => {
    videoTrackRef.current = videoTrack
  }, [videoTrack])

  // Create or stop video track when video is enabled/disabled or device changes
  useEffect(() => {
    let isActive = true

    const manageVideoTrack = async () => {
      try {
        // Stop existing video track if present
        const currentTrack = videoTrackRef.current
        if (currentTrack) {
          currentTrack.stop()
          videoTrackRef.current = null
          setVideoTrack(null)
        }

        // Only create new video track if enabled
        if (videoEnabled && isActive) {
          const newVideoTrack = await createLocalVideoTrack({
            deviceId: videoDeviceId || undefined,
            resolution: VideoPresets.h720.resolution,
          })

          // Check if still active before setting
          if (isActive) {
            setVideoTrack(newVideoTrack)
          } else {
            newVideoTrack.stop()
          }
        }
      } catch (e) {
        // Handle error silently
      }
    }

    manageVideoTrack()

    return () => {
      isActive = false
      const currentTrack = videoTrackRef.current
      if (currentTrack) {
        currentTrack.stop()
        videoTrackRef.current = null
      }
    }
  }, [videoEnabled, videoDeviceId, openPrivacyPolicy, loading])

  // Apply background when track or background changes
  useEffect(() => {
    if (videoTrack && backgroundType) {
      const applyBackgroundDirectly = async () => {
        try {
          // Import the function dynamically to avoid issues
          const { applyVirtualBackgroundToTrack } =
            await import('modules/meetings/lib/utils/virtualBackgroundUtils')

          // Add a delay to ensure track is ready
          setTimeout(async () => {
            // Apply background to video track
            if (videoTrack) {
              await applyVirtualBackgroundToTrack(videoTrack, backgroundType)
            }
          }, 200)
        } catch (error) {
          // Handle error silently
        }
      }

      applyBackgroundDirectly()
    }
  }, [videoTrack, backgroundType])

  // Get facing mode for camera
  const facingMode = useMemo(() => {
    if (videoTrack) {
      const { facingMode: facingMod } = facingModeFromLocalTrack(videoTrack)
      return facingMod
    }
    return 'undefined'
  }, [videoTrack])

  // Attach/detach video track to video element
  useEffect(() => {
    if (videoRef.current && videoTrack) {
      videoTrack.attach(videoRef.current)
    }

    return () => {
      if (videoTrack) {
        videoTrack.detach()
      }
    }
  }, [videoTrack])

  // Cleanup video track on unmount
  useEffect(() => {
    return () => {
      const currentTrack = videoTrackRef.current
      if (currentTrack) {
        currentTrack.stop()
        setVideoTrack(null)
      }
    }
  }, [])

  // Determine if we need to mirror the video based on facing mode
  const shouldMirror = facingMode !== 'environment'

  return (
    <Box
      width="full"
      overflowX="hidden"
      overflowY="hidden"
      style={{
        paddingTop: 'calc(3 / 5 * 100%)',
      }}
      alignItems="center"
      justifyContent="center"
      position="relative"
      borderRadius={{ all: 'abs016' }}
      background="grey1000"
    >
      <Box position="absolute" alignItems="center" justifyContent="center" style={{ inset: '0px' }}>
        {videoTrack && videoEnabled ? (
          <video
            data-lk-facing-mode={facingMode}
            ref={videoRef}
            width="100%"
            height="100%"
            autoPlay
            playsInline
            muted
            style={{
              objectFit: 'cover',
              objectPosition: 'center',
              transform: shouldMirror ? 'scaleX(-1)' : 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        ) : (
          <F0AvatarPerson
            firstName={firstName}
            lastName={lastName}
            size="xl"
            src={buildUrl(avatar)}
          />
        )}
      </Box>

      <Box
        position="absolute"
        paddingX="s12"
        paddingY="s8"
        borderRadius={{ all: 'abs012' }}
        background="grey1000"
        style={{
          top: '16px',
          left: '16px',
          zIndex: '1000',
        }}
      >
        <Text.Normal color="white" weight="bold" size="100">
          {username}
        </Text.Normal>
      </Box>
    </Box>
  )
}
